import { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Eye, Search, Calendar, User, MessageSquare, Send } from 'lucide-react';
import { getAdminMessages } from '@/api/services/adminService';

interface AdminMessage {
  _id: string;
  message: string;
  links: string[];
  sentBy: {
    _id: string;
    first_name: string;
    last_name: string;
    email: string;
  };
  sentByName: string;
  recipients: Array<{
    _id: string;
    first_name: string;
    last_name: string;
    email: string;
  }>;
  recipientFilters: {
    all: boolean;
    universities: string[];
    campuses: string[];
    hobbies: string[];
    userRoles: string[];
    selectedUserIds?: string[];
  };
  recipientCount: number;
  status: 'pending' | 'sent' | 'failed';
  sentAt?: Date;
  createdAt: Date;
  updatedAt: Date;
}

interface AdminMessagesResponse {
  success: boolean;
  data: AdminMessage[];
  pagination: {
    total: number;
    page: number;
    pages: number;
  };
}

export default function AdminMessageHistoryPage() {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const limit = 10;
  const toggleExpand = useCallback((id: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  }, []);

  const { data, isLoading, error, refetch } = useQuery<AdminMessagesResponse>({
    queryKey: ['adminMessages', currentPage, limit],
    queryFn: () => getAdminMessages({ page: currentPage, limit }),
    enabled: true, // Enable the query
  });

  const filteredMessages = data?.data?.filter(message =>
    message.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
    message.sentByName.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'sent':
        return 'bg-green-500 hover:bg-green-600';
      case 'pending':
        return 'bg-yellow-500 hover:bg-yellow-600';
      case 'failed':
        return 'bg-red-500 hover:bg-red-600';
      default:
        return 'bg-gray-500 hover:bg-gray-600';
    }
  };

  const getFilterDescription = (filters: AdminMessage['recipientFilters']) => {
    const descriptions = [];
    
    if (filters.all) {
      descriptions.push('All users');
    } else {
      if (filters.universities?.length > 0) {
        descriptions.push(`${filters.universities.length} universities`);
      }
      if (filters.campuses?.length > 0) {
        descriptions.push(`${filters.campuses.length} campuses`);
      }
      if (filters.hobbies?.length > 0) {
        descriptions.push(`${filters.hobbies.length} interests`);
      }
      if (filters.userRoles?.length > 0) {
        descriptions.push(`Roles: ${filters.userRoles.join(', ')}`);
      }
    }
    
    return descriptions.length > 0 ? descriptions.join(', ') : 'No filters';
  };

  if (error) {
    return (
      <div className="container mx-auto p-6">
        <Card className="border-red-200">
          <CardHeader>
            <CardTitle className="text-red-600">Error Loading Messages</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-red-600 mb-4">Failed to load admin messages. Please try again.</p>
            <Button onClick={() => refetch()} variant="outline">
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <DashboardLayout>
      <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col space-y-4 md:flex-row md:space-y-0 md:space-x-4 md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Message History</h1>
          <p className="text-muted-foreground">
            View all admin messages sent to users
          </p>
        </div>
        
        <div className="flex items-center space-x-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              placeholder="Search messages..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="pl-10 w-64"
            />
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      {data && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Messages</CardTitle>
              <MessageSquare className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{data?.pagination?.total || 0}</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Sent</CardTitle>
              <Send className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">
                {(data?.data || []).filter(m => m.status === 'sent').length}
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Pending</CardTitle>
              <Calendar className="h-4 w-4 text-yellow-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-yellow-600">
                {(data?.data || []).filter(m => m.status === 'pending').length}
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Failed</CardTitle>
              <User className="h-4 w-4 text-red-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">
                {(data?.data || []).filter(m => m.status === 'failed').length}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Messages List */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Messages</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="space-y-4 p-6">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex items-center space-x-4">
                  <Skeleton className="h-12 w-12 rounded-full" />
                  <div className="space-y-2 flex-1">
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-4 w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredMessages.length === 0 ? (
            <div className="text-center py-8">
              <MessageSquare className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No messages found</h3>
              <p className="mt-1 text-sm text-gray-500">
                {searchTerm ? 'Try adjusting your search.' : 'No admin messages have been sent yet.'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {filteredMessages.map((message) => (
                <div key={message._id} className="p-6 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-3 mb-2">
                        <Badge className={getStatusColor(message.status)}>
                          {message.status}
                        </Badge>
                        <span className="text-sm text-gray-500">
                          {format(new Date(message.createdAt), 'MMM d, yyyy HH:mm')}
                        </span>
                      </div>
                      
                      <h3 className="text-sm font-medium text-gray-900 mb-1">
                        From: {message.sentByName}
                      </h3>
                      
                      <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                        {message.message}
                      </p>
                      
                      {message.links && message.links.length > 0 && (
                        <div className="mb-2">
                          <span className="text-xs text-blue-600">
                            📎 {message.links.length} link(s) attached
                          </span>
                        </div>
                      )}
                      
                      <div className="flex items-center space-x-4 text-xs text-gray-500">
                        <span>Recipients: {message.recipientCount}</span>
                        <span>•</span>
                        <span>{getFilterDescription(message.recipientFilters)}</span>
                        {message.sentAt && (
                          <>
                            <span>•</span>
                            <span>Sent: {format(new Date(message.sentAt), 'HH:mm')}</span>
                          </>
                        )}
                      </div>
                    </div>
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleExpand(message._id)}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  </div>
                  {expanded.has(message._id) && (
                  <div className="mt-4 border-t pt-4 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-500">Status</label>
                        <div className="mt-1">
                          <Badge className={getStatusColor(message.status)}>
                            {message.status}
                          </Badge>
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Recipients</label>
                        <p className="mt-1 text-sm">{message.recipientCount} users</p>
                      </div>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-500">Sent By</label>
                      <p className="mt-1 text-sm">{message.sentByName}</p>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-500">Message</label>
                      <p className="mt-1 text-sm bg-gray-50 p-3 rounded">{message.message}</p>
                    </div>

                    {message.links && message.links.length > 0 && (
                      <div>
                        <label className="text-sm font-medium text-gray-500">Links</label>
                        <div className="mt-1 space-y-1">
                          {message.links.map((link, index) => (
                            <a
                              key={index}
                              href={link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-blue-600 hover:underline block"
                            >
                              {link}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}

                    <div>
                      <label className="text-sm font-medium text-gray-500">Target Filters</label>
                      <p className="mt-1 text-sm">{getFilterDescription(message.recipientFilters)}</p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-500">Created</label>
                        <p className="mt-1 text-sm">{format(new Date(message.createdAt), 'MMM d, yyyy HH:mm:ss')}</p>
                      </div>
                      {message.sentAt && (
                        <div>
                          <label className="text-sm font-medium text-gray-500">Sent</label>
                          <p className="mt-1 text-sm">{format(new Date(message.sentAt), 'MMM d, yyyy HH:mm:ss')}</p>
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-500">Recipients ({message.recipientCount})</label>
                      <div className="mt-1 max-h-40 overflow-y-auto bg-gray-50 rounded p-3">
                        {message.recipients && message.recipients.length > 0 ? (
                          <div className="space-y-2">
                            {message.recipients.map((recipient, index) => (
                              <div key={recipient._id || index} className="flex items-center space-x-2 text-sm">
                                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                  <User className="h-4 w-4 text-blue-600" />
                                </div>
                                <div>
                                  <p className="font-medium">{recipient.first_name} {recipient.last_name}</p>
                                  <p className="text-gray-500 text-xs">{recipient.email}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500">No detailed recipient information available</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {data && (data.pagination?.pages || 0) > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-700">
            Showing page {data.pagination?.page || 1} of {data.pagination?.pages || 1} 
            ({data.pagination?.total || 0} total messages)
          </p>
          
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </Button>
            
            <div className="flex items-center space-x-1">
              {[...Array(Math.min(5, data.pagination?.pages || 1))].map((_, i) => {
                const pageNum = i + 1;
                return (
                  <Button
                    key={pageNum}
                    variant={currentPage === pageNum ? "default" : "outline"}
                    size="sm"
                    onClick={() => setCurrentPage(pageNum)}
                  >
                    {pageNum}
                  </Button>
                );
              })}
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(prev => Math.min(data.pagination?.pages || 1, prev + 1))}
              disabled={currentPage === (data.pagination?.pages || 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}

            </div>
    </DashboardLayout>
  );
}